/* iter55z-o — DunningBanner: slim amber strip that appears at the top
 * of the writing suite the moment Stripe reports the user's
 * subscription is `past_due` (or `unpaid`). One click opens Stripe's
 * hosted Billing Portal so the user can update the card — no card
 * data ever hits our server.
 *
 * Reads `/api/subscriptions/me` on mount and again every 5 minutes.
 * Renders nothing when status is null / active / trialing / cancelled.
 * User can dismiss for the session via localStorage key.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, ExternalLink, X, Loader2 } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const POLL_MS = 5 * 60_000;
const DISMISS_KEY = 'dunning-banner-dismissed-at';
const DISMISS_MS = 24 * 60 * 60_000;   // banner re-appears next day

export default function DunningBanner() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(DISMISS_KEY);
    if (stored && Date.now() - Number(stored) < DISMISS_MS) setDismissed(true);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/subscriptions/me`, { withCredentials: true });
        if (!cancelled) setStatus(r.data);
      } catch { if (!cancelled) setStatus(null); }
    };
    load();
    const iv = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  if (!status || dismissed) return null;
  if (!['past_due', 'unpaid', 'incomplete'].includes(status.status)) return null;

  const openPortal = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/subscriptions/portal`,
        { return_url: window.location.href }, { withCredentials: true });
      if (r.data?.url) window.location.assign(r.data.url);
      else throw new Error('No portal URL');
    } catch {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="dunning-banner"
      data-status={status.status}
      className="flex items-center gap-2 border-b border-amber-400/70 bg-amber-100 px-4 py-1.5 text-[12px] text-amber-900"
    >
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
      <span className="font-semibold">
        Your last payment could not be processed.
      </span>
      <span className="hidden sm:inline">
        Update your card to keep your <b>{status.plan?.name || status.tier}</b> subscription active.
      </span>
      <button
        type="button"
        onClick={openPortal}
        disabled={busy}
        className="ml-auto inline-flex items-center gap-1 rounded-sm bg-amber-900 px-2.5 py-1 text-[11px] font-semibold text-amber-50 hover:bg-amber-950 disabled:opacity-60"
        data-testid="dunning-banner-fix"
      >
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />}
        Update card
      </button>
      <button
        type="button"
        onClick={() => {
          localStorage.setItem(DISMISS_KEY, String(Date.now()));
          setDismissed(true);
        }}
        className="rounded-sm p-1 hover:bg-amber-200"
        aria-label="Dismiss"
        data-testid="dunning-banner-dismiss"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
